import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import {
  calculateUVBounds, createDoubleSidedGeometry,
  createTextureMaterial,
  flipY,
  isMesh,
  isMeshStandardMaterial,
  until,
  type UVBounds
} from './util.ts';
import type {
  ID,
  FabricPath,
  MaterialInfo,
  ViewerOptions,
  ViewSide,
  ViewState,
  FabricResult
} from './typing.ts';
import { Mutex } from 'async-mutex';
import { Environment } from './environment.ts';
import {
  FabricLoader
} from './loader.ts';

export class Viewer {
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;
  private controls!: OrbitControls;

  private raycaster!: THREE.Raycaster;
  private mouse!: THREE.Vector2;

  private options: ViewerOptions;

  private lastGlbScene: THREE.Group | undefined;

  private selectedMaterialID: string = '';

  private textureMap: Map<string, FabricResult> = new Map();

  private boundsMap: Map<string, UVBounds> = new Map();

  private materialList: THREE.Mesh[] = [];

  private bbox: THREE.Box3 = new THREE.Box3();

  private renderFlag = false;

  private mutex = new Mutex();

  private fabricLoader = new FabricLoader();

  constructor(options: ViewerOptions) {
    this.options = options;

    this.initScene();
    this.initAxesHelper();
    this.initCamera();
    this.initRenderer();
    this.initLight();
    this.initControls();
    this.addEventListener();

    this.animate();
  }

  public async load(src: string) {
    try {
      this.dispose();

      const gltfLoader = new GLTFLoader();
      const gltf = await gltfLoader.loadAsync(`${src}`,
        (event) => {
          const progress = event.loaded / event.total * 100;
          const loaded = Math.abs(progress - 100) < Number.EPSILON;
          this.options.events?.onProgress?.(progress, loaded);
        });

      gltf.scene.traverse((object) => {
        if (isMesh(object)) {
          this.materialList.push(object);
        }
      });

      gltf.scene.position.set(0, 0, 0);

      this.bbox = new THREE.Box3().setFromObject(gltf.scene);
      const center = this.bbox.getCenter(new THREE.Vector3());
      await this.setCameraViewState();

      gltf.scene.translateX(-center.x);
      gltf.scene.translateY(-center.y);
      gltf.scene.translateZ(-center.z);

      this.lastGlbScene = gltf.scene;
      this.scene.add(gltf.scene);

      this.options.events?.onReady?.();
      return true;
    } catch (error) {
      console.error('Error loading resources:', error);
      return false;
    }
  }

  private transformMeshUV(mesh: THREE.Mesh, bounds: UVBounds) {
    if (bounds?.transformed) {
      return;
    }

    const geometry = mesh.geometry;
    const uvAttribute = geometry.attributes.uv;

    if (uvAttribute) {
      for (let i = 0; i < uvAttribute.count; i++) {
        const u = uvAttribute.getX(i) - bounds.uv.min.u;
        const v = bounds.uv.max.v - uvAttribute.getY(i); // flip V
        uvAttribute.setXY(i, u, v);
      }
      uvAttribute.needsUpdate = true;

      const uv = {
        ...bounds.uv,
        min: {
          u: 0,
          v: bounds.uv.max.v - bounds.uv.min.v
        },
        max: {
          u: bounds.uv.max.u - bounds.uv.min.u,
          v: 0
        }
      };

      this.boundsMap.set(mesh.uuid, { ...bounds, uv, transformed: true });
    }
  }

  public async changeFabric(fabricInfo: FabricPath) {
    return await this.changeFabricByMaterial(this.selectedMaterialID, fabricInfo);
  }

  public async changeFabricColor(color: string) {
    const mesh = this.materialList.find((material) => material.uuid === this.selectedMaterialID);
    if (!mesh) {
      return false;
    }
    this.changeMeshColor(mesh, color);

    return true;
  }

  public async changeFabricByMaterial(materialId: ID, fabricInfo?: FabricPath, color?: string): Promise<boolean> {
    const mesh = this.materialList.find((material) => material.uuid === materialId);
    if (!mesh) {
      return false;
    }

    const bounds = this.getUVBounds(mesh);
    if (!bounds) {
      return false;
    }

    if (fabricInfo) {
      this.transformMeshUV(mesh, bounds);

      const texture = await this.getTexture(fabricInfo);
      if (texture) {
        const material = createTextureMaterial(texture, bounds, color);

        if (Array.isArray(material)) {
          if (mesh.geometry.groups.length == 0) {
            const newGeometry = createDoubleSidedGeometry(mesh.geometry);
            const oldGeometry = mesh.geometry;
            mesh.geometry = newGeometry;
            oldGeometry.dispose();
          }
          mesh.material = material;
        } else {
          console.log(material);
          mesh.material = material;
        }
        return true;
      }
    }

    if (color) {
      this.changeMeshColor(mesh, color);
      return true;
    }

    return false;
  }

  public async changeFabricsByMaterials(materialIds: ID[], fabricInfos?: FabricPath[], colors?: string[]): Promise<boolean> {
    for (let index = 0; index < materialIds.length; index++) {
      const materialId = materialIds[index]!;
      const result = await this.changeFabricByMaterial(materialId, fabricInfos?.[index], colors?.[index]);
      if (!result) return false;
    }
    return true;
  }

  public async getFabricState() {
    const mesh = this.materialList.find((material) => material.uuid === this.selectedMaterialID);
    if (!mesh) {
      return {
        offsetX: 0,
        offsetY: 0
      };
    }
    const material = mesh.material;
    const materials = Array.isArray(material) ? material : [material];
    if (materials[0] && isMeshStandardMaterial(materials[0])) {
      return {
        offsetX: materials[0].map?.offset.x || 0,
        offsetY: materials[0].map?.offset.y || 0
      };
    }

    return {
      offsetX: 0,
      offsetY: 0
    };
  }

  public async getMaterialList(): Promise<MaterialInfo[]> {
    return this.materialList
      .map((material) => ({
        id: material.uuid,
        name: material.name,
        type: material.type
      }));
  }

  public async getScreenshot(side: ViewSide = 1, _keepScale: boolean = true, saveBackground: boolean = false) {
    const release = await this.mutex.acquire();
    try {
      const originalBackground = this.scene.background;
      if (!saveBackground) {
        this.scene.background = null;
      }

      if (side) {
        await this.setCameraViewState((side - 1) as ViewState);
      }

      const dpr = window.devicePixelRatio || 1;

      const width = Math.floor(window.innerWidth * dpr);
      const height = Math.floor(window.innerHeight * dpr);

      const renderTarget = new THREE.WebGLRenderTarget(width, height, {
        colorSpace: THREE.SRGBColorSpace
      });

      this.renderer.setRenderTarget(renderTarget);
      this.renderer.render(this.scene, this.camera);
      this.renderer.setRenderTarget(null);

      // Create a new Uint8Array to hold the pixel data
      const buffer = new Uint8Array(width * height * 4); // RGBA
      this.renderer.readRenderTargetPixels(renderTarget, 0, 0, width, height, buffer);

      renderTarget.dispose();
      this.scene.background = originalBackground;

      flipY(buffer, width, height);

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext('2d');
      if (!context) {
        return '';
      }
      const imageData = context.createImageData(width, height);
      imageData.data.set(buffer);
      context.putImageData(imageData, 0, 0);

      return canvas.toDataURL('image/png');
    } finally {
      release();
    }
  }

  public async selectMaterial(id: ID, highlightDelayMillisecond: number = 400, autoLocateEnabled: boolean = true, flashTimes: number = 1): Promise<boolean> {
    const mesh = this.materialList.find((material) => material.uuid === id);
    if (!mesh || !isMesh(mesh)) {
      return false;
    }

    this.selectedMaterialID = id;
    this.highlightMesh(mesh, highlightDelayMillisecond, autoLocateEnabled, flashTimes);
    return true;
  }

  public async setCameraViewState(viewState: ViewState = 0) {
    this.renderFlag = false;

    const bboxSize = this.bbox.getSize(new THREE.Vector3());
    const distance = 1.666667 * Math.max(bboxSize.x, bboxSize.y);
    switch (viewState) {
      case 0:
        // front
        this.camera.position.set(0, 0, distance);
        break;
      case 1:
        // left
        this.camera.position.set(-distance, 0, 0);
        break;
      case 2:
        // right
        this.camera.position.set(distance, 0, 0);
        break;
      case 3:
        // back
        this.camera.position.set(0, 0, -distance);
        break;
      case 4:
        // top
        this.camera.position.set(0, distance, 0);
        break;
      case 5:
        // bottom
        this.camera.position.set(0, -distance, 0);
        break;
    }
    await until(() => this.renderFlag);
  }

  public async setFabricOffsetX(offsetX: number) {
    const mesh = this.materialList.find((material) => material.uuid === this.selectedMaterialID);
    if (!mesh) {
      return false;
    }

    this.renderFlag = false;

    const material = mesh.material;
    const materials = Array.isArray(material) ? material : [material];
    materials.forEach(m => {
      if (isMeshStandardMaterial(m) && m.map) {
        m.map.offset.x = offsetX;
        if (m.alphaMap) {
          m.alphaMap.offset.x = offsetX;
        }
        if (m.displacementMap) {
          m.displacementMap.offset.x = offsetX;
        }
        if (m.normalMap) {
          m.normalMap.offset.x = offsetX;
        }
        m.needsUpdate = true;
      }
    });

    await until(() => this.renderFlag);
    return true;
  }

  public async setFabricOffsetY(offsetY: number) {
    const mesh = this.materialList.find((material) => material.uuid === this.selectedMaterialID);
    if (!mesh) {
      return false;
    }

    this.renderFlag = false;

    const material = mesh.material;
    const materials = Array.isArray(material) ? material : [material];
    materials.forEach(m => {
      if (isMeshStandardMaterial(m) && m.map) {
        m.map.offset.y = offsetY;
        if (m.alphaMap) {
          m.alphaMap.offset.y = offsetY;
        }
        if (m.displacementMap) {
          m.displacementMap.offset.y = offsetY;
        }
        if (m.normalMap) {
          m.normalMap.offset.y = offsetY;
        }
        m.needsUpdate = true;
      }
    });

    await until(() => this.renderFlag);
    return true;
  }

  private initScene() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(this.options.custom?.backgroundStyle.color ?? 0xa0a0a0);

    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
  }

  private initAxesHelper() {
    // const axesHelper = new THREE.AxesHelper(50);
    // this.scene.add(axesHelper);
  }

  private initLight() {
    const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pmremGenerator.fromScene(new Environment(), 0.4).texture;
  }

  private initCamera() {
    this.camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.001, 10000);
  }

  private initRenderer() {
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    if (this.options.dom) {
      this.options.dom.appendChild(this.renderer.domElement);
    } else {
      document.body.appendChild(this.renderer.domElement);
    }
  }

  private initControls() {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.target.set(0, 0, 0);
  }

  private animate() {
    requestAnimationFrame(() => this.animate());

    this.controls.update();
    this.renderer.render(this.scene, this.camera);
    this.renderFlag = true;
  }

  private async getTexture(t: FabricPath) {
    if (!this.textureMap.has(t.id)) {
      const fabricInfo = await this.fabricLoader.loadAsync(JSON.stringify(t)) as FabricResult;
      if (fabricInfo) {
        this.textureMap.set(t.id, fabricInfo);
      }
    }
    return this.textureMap.get(t.id);
  }

  private getUVBounds(mesh: THREE.Mesh) {
    if (!this.boundsMap.has(mesh.uuid)) {
      const bounds = calculateUVBounds(mesh);
      if (bounds) {
        this.boundsMap.set(mesh.uuid, bounds);
      }
    }
    return this.boundsMap.get(mesh.uuid);
  }

  private changeMeshColor(mesh: THREE.Mesh, color: string) {
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    materials.forEach((material) => {
      if (isMeshStandardMaterial(material)) {
        material.color.set(color);
      }
    });
  }

  private highlightMesh(mesh: THREE.Mesh, highlightDelayMillisecond: number = 100, autoLocateEnabled: boolean = false, flashTimes: number = 1) {
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    materials.forEach((material) => {
      if (isMeshStandardMaterial(material)) {
        const currentColor = material.color.clone();
        const brightColor = currentColor.clone().multiplyScalar(1.5);

        let count = 0;
        const flashInterval = setInterval(() => {
          material.color.set(count % 2 === 0 ? brightColor : currentColor);
          count++;

          // Stop after flashing 'flashTimes' times
          if (count >= flashTimes * 2) {
            clearInterval(flashInterval);
            material.color.set(currentColor);
          }
        }, highlightDelayMillisecond);

        if (autoLocateEnabled) {
          // TODO Auto locate the mesh
          console.log('wip: Auto locate the mesh');
        }
      }
    });
  }

  private dispose() {
    if (this.lastGlbScene) {
      this.materialList = [];

      this.lastGlbScene.traverse((object) => {
        if (isMesh(object)) {
          object.geometry.dispose();

          const materials = Array.isArray(object.material) ? object.material : [object.material];
          materials.forEach((material) => material.dispose());
          this.lastGlbScene?.remove(object);
        }
      });
      this.scene.remove(this.lastGlbScene);
      this.textureMap
        .forEach((value) => {
          const { front, back } = value;
          front.baseColor.texture.dispose();
          front.alpha?.texture.dispose();
          front.displacement?.texture.dispose();
          front.normal?.texture.dispose();

          back?.baseColor.texture.dispose();
          back?.alpha?.texture.dispose();
          back?.displacement?.texture.dispose();
          back?.normal?.texture.dispose();
        });
      this.textureMap.clear();
      this.boundsMap.clear();
    }
  }

  private addEventListener() {
    window.addEventListener('resize', () => {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    });

    window.addEventListener('click', (event) => {
      if (event.target !== this.renderer.domElement) {
        event.stopPropagation();
        return;
      }

      this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

      this.raycaster.setFromCamera(this.mouse, this.camera);

      const intersects = this.raycaster.intersectObjects(this.scene.children, true);
      if (intersects.length > 0) {
        const selectedObject = intersects[0]!.object;
        this.selectedMaterialID = selectedObject.uuid;

        this.options.events?.onMaterialSelected?.({
          infoId: selectedObject.uuid,
          materialInfo: {
            type: selectedObject.type,
            id: selectedObject.uuid,
            name: selectedObject.name
          }
        }, true);

        if (isMesh(selectedObject)) {
          this.highlightMesh(selectedObject);
        }
      } else {
        this.options.events?.onNothingSelected?.();
        this.selectedMaterialID = '';
      }
    });

    document.addEventListener('keydown', async (event) => {
      switch (event.key) {
        case '2':
          await this.setCameraViewState();
          break;
        case '4':
          await this.setCameraViewState(1);
          break;
        case '6':
          await this.setCameraViewState(2);
          break;
        case '8':
          await this.setCameraViewState(3);
          break;
        case '5':
          await this.setCameraViewState(4);
          break;
        case '0':
          await this.setCameraViewState(5);
          break;
      }
    });
  }
}
